import * as React from 'react';

import { IBasicStyledProps, SemanticColorKey } from '@notino/react-styleguide';

import { registerKeyboardListeners } from '@helpers/accessibility/registerKeyboardListeners';
import { arrayFromNumber } from '@helpers/utils';

import { Bar, Progress, Segment, Segments } from './styled';

interface IProgressBarProps extends IBasicStyledProps {
  max: number;
  value: number;
  segments?: boolean;
  selectable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelect?: any;
  color?: SemanticColorKey;
  height?: number;
  animationDuration?: number;
  animationDelay?: number;
  name: string;
  label?: string;
}

const ProgressBar = ({
  selectable,
  onSelect,
  segments,
  color,
  height,
  name,
  value,
  max,
  animationDuration,
  label,
  ...rest
}: IProgressBarProps) => {
  const calcWidth = (segment: number): number => {
    let percent = selectable ? segment * 20 : (value / max) * 100;

    if (segments) {
      percent = (percent > 0 ? Math.round(percent / 20) : 0) * 20;
    }

    if (percent > 100) {
      percent = 100;
    }

    return percent;
  };

  const [selectedSegment, setSelectedSegment] = React.useState(value);
  const [width, setWidth] = React.useState(calcWidth(value));

  const onSegmentClick = (segment) => {
    if (selectable) {
      onSelect(segment);
      setSelectedSegment(segment);
      setWidth(calcWidth(segment));
    }
  };

  const onSegmentHover = (segment) => {
    if (selectable) {
      setWidth(calcWidth(segment));
    }
  };

  const onProgressLeave = () => {
    if (selectable) {
      setWidth(calcWidth(selectedSegment));
    }
  };

  return (
    <Progress
      heightInPx={height}
      onMouseLeave={onProgressLeave}
      aria-label={label}
      {...rest}
    >
      {segments && (
        <Segments>
          {arrayFromNumber(5).map((i) => {
            const handleSelect = () => onSegmentClick(i);
            return (
              <Segment
                key={i}
                aria-label={`${i}`}
                data-cypress={`${name}_segment_${i}`}
                selectable={!!selectable}
                tabIndex={selectable ? 0 : -1}
                onClick={handleSelect}
                onMouseEnter={() => onSegmentHover(i)}
                {...registerKeyboardListeners({
                  Enter: handleSelect,
                  Space: handleSelect,
                })}
              />
            );
          })}
        </Segments>
      )}

      <Bar
        data-cypress={`${name}_progress_bar`}
        color={color}
        lineWidth={width}
        heightInPx={height}
        animationDuration={animationDuration}
      />
    </Progress>
  );
};

export default ProgressBar;
